import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { UpdateWorkspace } from '../../../../shared/types/workspaceTypes';
import axios from 'axios';
import { FlexCol } from '../../../../shared/FlexCol';
import { FlexRow } from '../../../../shared/FlexRow';
import { Tag } from '../../../../shared/styled-components/styled_tags';
import { Body, Headline, Label } from '../../../../shared/styled-components/styled_text';
import { IconInput } from '../../../../shared/components/IconInput';
import { getApiUrl, getCandidatreeName } from '../../../../common/api/apiClient';
import { getSavedAuth } from '../../../../common/utils/storage';
import { useTrack } from '@hackler/react-sdk';

interface Props {
  data: UpdateWorkspace;
  onChange: (data: UpdateWorkspace) => void;
  previewClick?: () => void;
}

export const OnboardingStep2: React.FC<Props> = ({ data, onChange, previewClick }) => {
  const track = useTrack();
  const [treeValue, setTreeValue] = useState<string>(data.tree_signature ?? '');
  const [errorMessage, setErrorMessage] = useState<string>(data.tree_error ?? '');

  useEffect(() => {
    track({ key: 'view_workspaces_step2' });

    const getTempName = async () => {
      const tempName = await getCandidatreeName(data.name);
      setTreeValue(tempName.converted);
      onChange({ ...data, tree_signature: tempName.converted });
    };

    if (!data.tree_signature) {
      void getTempName();
    }
  }, []);

  useEffect(() => {
    const handleInputChange = async (signature: string) => {
      await axios.get(`${getApiUrl()}application-link/signature/check?q=${signature}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getSavedAuth()?.access_token}`,
        },
      });
    };

    const signature = treeValue?.trim();
    if (!signature) {
      if (errorMessage) {
        setErrorMessage('');
      }
      onChange({ ...data, tree_signature: '', tree_error: '' });
      return;
    }

    if (signature.length > 20) {
      setErrorMessage('최대 20자까지 입력할 수 있어요.');
      onChange({ ...data, tree_signature: treeValue, tree_error: errorMessage });
      return;
    }

    handleInputChange(signature)
      .then(r => {
        setErrorMessage('');
        onChange({ ...data, tree_signature: treeValue, tree_error: '' });
      })
      .catch(e => {
        if (e.response?.status === 409) {
          setErrorMessage('이미 사용 중인 URL 주소예요.');
          onChange({ ...data, tree_signature: treeValue, tree_error: '이미 사용 중인 URL 주소예요.' });
        } else {
          if ((e.response?.data?.message ?? '').includes('20')) {
            setErrorMessage('최대 20자까지 입력할 수 있어요.');
            onChange({ ...data, tree_signature: treeValue, tree_error: '최대 20자까지 입력할 수 있어요.' });
          } else {
            setErrorMessage('형식에 맞게 다시 입력해주세요.(한글, 영어 소문자, 숫자, ‘-’하이픈)');
            onChange({
              ...data,
              tree_signature: treeValue,
              tree_error: '형식에 맞게 다시 입력해주세요.(한글, 영어 소문자, 숫자, ‘-’하이픈)',
            });
          }
        }
      });
  }, [treeValue]);

  return (
    <Container>
      <FlexCol width={'100%'} justify={'flex-start'} align={'flex-start'} gap={'0.8rem'}>
        <FlexRow width={'100%'} justify={'flex-start'} align={'center'} gap={'0.6rem'}>
          <Tag color={'mono'}>선택</Tag>
          <Headline size={'m'}>채용 사이트 생성하기</Headline>
        </FlexRow>
        <Body size={'m'} style={{ color: ' var(--content-color-secondary)' }}>
          지원자가 회사 고유 채용 사이트로 지원자 지원서를
          <br />
          제출하면 대시보드에 자동 등록돼요.
        </Body>
      </FlexCol>
      <FlexCol align={'flex-start'} width={'100%'} gap={'0.4rem'}>
        <IconInput
          label={
            <Body size={'s'} style={{ color: ' var(--content-color-secondary)' }}>
              apply.candidate.im/
            </Body>
          }
          value={data.tree_signature ?? ''}
          placeholder={'예) candidate-company'}
          hint={'한글, 영어 소문자, 숫자, ‘-’하이픈 조합'}
          max={20}
          showMaxCount
          onClick={() => {
            track({ key: 'click_input_application_link' });
          }}
          onChange={tree_signature => onChange({ ...data, tree_signature })}
        />
        {!!errorMessage && (
          <Body size={'s'} style={{ paddingLeft: '1.2rem', color: 'var(--error)' }}>
            {errorMessage}
          </Body>
        )}
      </FlexCol>
      <FlexCol width={'100%'} justify={'flex-start'} align={'center'} gap={'0.4rem'}>
        <Body size={'s'} style={{ color: ' var(--content-color-tertiary)' }}>
          채용 사이트가 어떻게 보이는지 궁금하신가요?
        </Body>
        <PreviewButton onClick={previewClick}>
          <Label size={'s'}>채용 사이트 예시 미리보기</Label>
        </PreviewButton>
      </FlexCol>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 3.2rem;
`;

const PreviewButton = styled.button`
  &:hover {
    text-decoration: underline;
    text-underline-offset: 0.4rem;
  }
`;
